/**
 * Bundle 165 · AdminBadgeContext.
 *
 * Provides an imperative `refetchLeakCount()` so admin pages
 * can invalidate the sidebar LEAK badge immediately after a
 * mutation that could change the count (backfill Apply,
 * manual delete). Consumer degrades to a no-op when the
 * provider is absent so older admin pages do not crash.
 *
 * V1 / V2 reminder sends intentionally do NOT invalidate -
 * they only stamp reminder timestamps and cannot change the
 * LEAK verdict per the shared `compute_verdict()` helper.
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

const AdminBadgeContext = createContext({
  leakCount: 0,
  setLeakCount: () => {},
  refetchLeakCount: () => {},
});

export const AdminBadgeProvider = ({ children }) => {
  const [leakCount, setLeakCount] = useState(0);

  const refetchLeakCount = useCallback(async () => {
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/incidents/tiles/incomplete-signups`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token') || ''}`,
          },
        },
      );
      if (!res.ok) return;
      const body = await res.json();
      setLeakCount(body.leak || 0);
    } catch (_) {
      // noop
    }
  }, []);

  const value = useMemo(() => ({
    leakCount, setLeakCount, refetchLeakCount,
  }), [leakCount, refetchLeakCount]);

  return (
    <AdminBadgeContext.Provider
      value={value}
      data-testid="admin-badge-provider"
    >
      {children}
    </AdminBadgeContext.Provider>
  );
};

/**
 * Hook. Returns `{leakCount, refetchLeakCount}`. When the
 * provider is absent (older admin pages that have not been
 * wrapped) the returned `refetchLeakCount` is a no-op so
 * consumers can call it unconditionally without breaking.
 */
export const useAdminBadge = () => useContext(AdminBadgeContext);

export default AdminBadgeContext;
